import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const SESSION_WARNING_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [homes, setHomes] = useState([]);
  const [careers, setCareers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedPermissions = JSON.parse(localStorage.getItem('adminPermissions') || '[]');
    
    if (!token) {
      console.log('No token found, redirecting to login');
      navigate('/admin/login');
      return;
    }

    // Prevent multiple initializations
    if (permissions.length > 0) {
      console.log('Dashboard already initialized, skipping...');
      return;
    }

    console.log('Initializing dashboard with permissions:', storedPermissions);
    setPermissions(storedPermissions);

    // Check session validity
    const initializeSession = async () => {
      try {
        await checkSession();
        
        // Only fetch data if session check passes
        if (storedPermissions.includes('posts')) {
          await fetchPosts();
        }
        if (storedPermissions.includes('homes')) {
          await fetchHomes();
        }
        if (storedPermissions.includes('careers')) {
          await fetchCareers();
        }
      } catch (error) {
        console.error('Failed to initialize session:', error);
        handleLogout();
      }
    };

    initializeSession();

    // Set up periodic session checks
    const sessionCheckInterval = setInterval(checkSession, 60000); // Check every minute

    // Set up session warning
    const warningTimeout = setTimeout(() => setShowSessionWarning(true), SESSION_WARNING_TIME);

    // Cleanup intervals on component unmount
    return () => {
      clearInterval(sessionCheckInterval);
      clearTimeout(warningTimeout);
    };
  }, [navigate]);

  const checkSession = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      console.log('No token found in localStorage');
      handleLogout();
      return;
    }

    try {
      console.log('Checking session with token:', token.substring(0, 20) + '...');
      const response = await fetch(`${BASE_URL}/api/admin/check-session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Session check failed:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
        
        // Check if it's specifically a session not found error
        if (errorData.details === 'No active session found') {
          console.log('No active session found, attempting to refresh...');
          await refreshSession();
          return;
        }
        
        handleLogout();
      } else {
        console.log('Session check successful');
      }
    } catch (error) {
      console.error('Session check failed with error:', error);
      handleLogout();
    }
  };

  const refreshSession = async () => {
    try {
      const username = localStorage.getItem('adminRole');
      const currentToken = localStorage.getItem('adminToken');
      
      if (!username || !currentToken) {
        console.error('Missing username or token for session refresh');
        handleLogout();
        return;
      }

      console.log('Attempting to refresh session for user:', username);
      
      // Since refresh-token endpoint doesn't exist, try to re-login directly
      const loginResponse = await fetch(`${BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: localStorage.getItem('adminPassword')
        }),
        credentials: 'include'
      });

      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        if (loginData.token) {
          localStorage.setItem('adminToken', loginData.token);
          if (loginData.permissions) {
            localStorage.setItem('adminPermissions', JSON.stringify(loginData.permissions));
            setPermissions(loginData.permissions);
          }
          console.log('Re-login successful');
          setShowSessionWarning(false);
          setTimeout(() => setShowSessionWarning(true), SESSION_WARNING_TIME);
        } else {
          console.error('No token received in login response');
          handleLogout();
        }
      } else {
        const errorData = await loginResponse.json().catch(() => ({}));
        console.error('Re-login failed:', loginResponse.status, errorData);
        
        // If we get a 403, it means the credentials are invalid
        if (loginResponse.status === 403) {
          console.log('Invalid credentials, redirecting to login');
          handleLogout();
          return;
        }
        
        // For other errors, try to continue with the current session
        console.log('Continuing with current session despite refresh failure');
      }
    } catch (error) {
      console.error('Session refresh failed with error:', error);
      // Don't logout on network errors, try to continue with current session
      console.log('Continuing with current session despite refresh error');
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/posts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchHomes = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/homes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      setHomes(data);
    } catch (error) {
      console.error('Error fetching homes:', error);
    }
  };

  const fetchCareers = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/careers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      setCareers(data);
    } catch (error) {
      console.error('Error fetching careers:', error);
    }
  };

  const handleDeletePost = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/deletepost/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleDeleteHome = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/deletehome/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (response.ok) {
        fetchHomes();
      }
    } catch (error) {
      console.error('Error deleting home:', error);
    }
  };

  const handleDeleteCareer = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/deletecareer/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (response.ok) {
        fetchCareers();
      }
    } catch (error) {
      console.error('Error deleting career:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        await fetch(`${BASE_URL}/api/admin/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }).catch(error => {
          console.error('Logout request failed:', error);
        });
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Clear all admin-related data
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminPermissions');
      localStorage.removeItem('adminPassword');
      navigate('/admin/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Session Warning Modal */}
      {showSessionWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Session Timeout Warning</h3>
            <p className="mb-4">Your session will expire soon. Would you like to stay logged in?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Logout
              </button>
              <button
                onClick={refreshSession}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <header className="shadow-sm">
        <div className="bg-gray-700 text-white mx-auto px-6 py-12 flex justify-between items-center">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <div className="space-x-4">
            {permissions.includes('posts') && (
              <button onClick={() => navigate('/admin/createpost')} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium">
                Add Post
              </button>
            )}
            {permissions.includes('homes') && (
              <button onClick={() => navigate('/admin/createhome')} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium">
                Add Home
              </button>
            )}
            {permissions.includes('careers') && (
              <button onClick={() => navigate('/admin/createcareer')} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium">
                Add Career
              </button>
            )}
            <button onClick={handleLogout} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Posts Section */}
        {permissions.includes('posts') && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Posts</h2>
            {posts.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <p className="text-gray-500 text-lg font-medium">No posts found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <div key={post.id} className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-gray-800">
                        {post.title}
                      </h3>
                      <span className="ml-2 inline-block px-3 py-1 rounded-full text-sm font-medium 
                                     bg-blue-500
                                     text-white capitalize">
                        {post.type}
                      </span>
                    </div>
                    <p className="text-gray-500 mb-4">By {post.writtenBy}</p>
                    <div className="flex space-x-2">
                      <Link 
                        to={`/ViewPost/${post.id}`} 
                        className="flex-1 text-center py-2 bg-primary-yellow hover:brightness-75 text-black rounded">
                        View
                      </Link>
                      <button
                        onClick={() => navigate(`/admin/editpost/${post.id}`)}
                        className="flex-1 text-center py-2 bg-primary hover:brightness-75 text-white rounded">
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="flex-1 text-center py-2 bg-secondary-blue hover:brightness-75 text-white rounded">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Homes Section */}
        {permissions.includes('homes') && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Homes</h2>
            {homes.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <p className="text-gray-500 text-lg font-medium">No homes found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {homes.map((home) => (
                  <div key={home.id} className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all">
                    <h3 className="text-2xl font-bold text-gray-800">{home.streetAddress}</h3>
                    <p className="text-gray-500 mt-2">Area: {home.area}</p>
                    <p className="text-gray-500 mt-2">Rent: {home.price}%</p>
                    <div className="flex space-x-2 mt-4">
                      <Link to={`/ViewHome/${home.id}`} className="flex-1 text-center py-2 bg-primary-yellow hover:brightness-75 text-black rounded">
                        View
                      </Link>
                      <button onClick={() => navigate(`/admin/edithome/${home.id}`)} className="flex-1 text-center py-2 bg-primary hover:brightness-75 text-white rounded">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteHome(home.id)} className="flex-1 text-center py-2 bg-secondary-blue hover:brightness-75 text-white rounded">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Careers Section */}
        {permissions.includes('careers') && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold mb-4">Careers</h2>
            {careers.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <p className="text-gray-500 text-lg font-medium">No job postings found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {careers.map((career) => (
                  <div key={career.id} className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-gray-800">{career.jobTitle}</h3>
                      <p className="text-gray-500">Job Code: {career.jobCode}</p>
                      <p className="text-gray-500">Location: {career.location}</p>
                      <p className="text-gray-500">Type: {career.jobType}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Link 
                        to={`/careers/${career.id}`} 
                        className="flex-1 text-center py-2 bg-primary-yellow hover:brightness-75 text-black rounded">
                        View
                      </Link>
                      <button
                        onClick={() => navigate(`/admin/editcareer/${career.id}`)}
                        className="flex-1 text-center py-2 bg-primary hover:brightness-75 text-white rounded">
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCareer(career.id)}
                        className="flex-1 text-center py-2 bg-secondary-blue hover:brightness-75 text-white rounded">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;